import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

function AddGuest() {
  const [guests, setGuests] = useState([
    { name: '', phoneNumber: '', ageCategory: 'adult', tableNumber: '', photo: null, comments: '', eveningGuest: false }
  ]);
  const [familyClan, setFamilyClan] = useState('');

  const handleChange = (index, field, value) => {
    const updated = [...guests];
    if (field === 'photo') {
      updated[index][field] = value;
    } else {
      updated[index][field] = value;
    }
    setGuests(updated);
  };

  const addGuest = () => {
    setGuests([...guests, { name: '', phoneNumber: '', ageCategory: 'adult', tableNumber: '', photo: null, comments: '', eveningGuest: false }]);
  };

  const removeGuest = (index) => {
    const updated = [...guests];
    updated.splice(index, 1);
    setGuests(updated.length > 0 ? updated : [{ name: '', phoneNumber: '', ageCategory: 'adult', tableNumber: '', photo: null, comments: '', eveningGuest: false }]);
  };

  const isAdult = (category) => category === 'adult';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const storage = getStorage();
    const familyId = uuidv4();

    // Add a unique guestId to each guest and upload photos
    const enrichedGuests = await Promise.all(
      guests.map(async (g) => {
        const guestId = uuidv4();
        let photoUrl = '';
        if (g.photo) {
          const photoRef = ref(storage, `guest_photos/${guestId}`);
          await uploadBytes(photoRef, g.photo);
          photoUrl = await getDownloadURL(photoRef);
        }

        return {
          ...g,
          guestId,
          photoUrl,
        };
      })
    );

    // Collect guestIds of those who are adults
    const over18 = enrichedGuests.filter(g => isAdult(g.ageCategory)).map(g => g.guestId);

    // Prepare batch with guestId and use guestId in editableBy
    const batch = enrichedGuests.map(g => {
      return {
        guestId: g.guestId,
        name: g.name,
        phoneNumber: g.phoneNumber,
        ageCategory: g.ageCategory,
        role: g.role || 'guest',
        tableNumber: g.tableNumber,
        familyClan,
        familyId,
        photoUrl: g.photoUrl,
        comments: g.comments,
        eveningGuest: g.eveningGuest === true,
        editableBy: isAdult(g.ageCategory) ? [] : over18
      };
    });

    for (let guest of batch) {
      await addDoc(collection(db, 'invites'), guest);
    }

    alert('Family group added!');
    setGuests([{ name: '', phoneNumber: '', ageCategory: 'adult', tableNumber: '', photo: null, comments: '', eveningGuest: false }]);
    setFamilyClan('');
  };

return (
    <div className="page-container">
      <h2 className="page-title">Add Family Group</h2>
      <p className="page-subtitle">
        Use this form to add one family group at a time. Enter each guest’s name, age category, phone number, and other details. 
        All guests submitted together will be treated as part of the same family. To add multiple family members, fill out their details and click 
        “Add Another Guest” for each person. Under-18 guests will automatically be editable by adult members of the same group.
      </p>
      <form onSubmit={handleSubmit}>
        <input
          className="input-field"
          style={{ width: '100%', marginBottom: '20px' }}
          placeholder="Family Clan"
          value={familyClan}
          onChange={(e) => setFamilyClan(e.target.value)}
          required
        />
        {guests.map((g, i) => (
          <div key={i} className="card" style={{ marginBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <input 
              className="input-field"
              style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }} 
              placeholder="Name" 
              value={g.name} 
              onChange={(e) => handleChange(i, 'name', e.target.value)} 
              required 
            />
            <select 
              className="select-field"
              style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }} 
              value={g.ageCategory} 
              onChange={(e) => handleChange(i, 'ageCategory', e.target.value)} 
              required
            >
              <option value="" disabled>Select Age Category</option>
              <option value="infant">Infant 0-2</option>
              <option value="child">Child 2-10</option>
              <option value="teen">Teen 11-17</option>
              <option value="adult">Adult 18+</option>
            </select>
            {!['infant', 'child'].includes(g.ageCategory) && (
              <input
                className="input-field"
                style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
                placeholder="Phone"
                value={g.phoneNumber}
                onChange={(e) => handleChange(i, 'phoneNumber', e.target.value)}
              />
            )}
            <input 
              className="input-field"
              style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }} 
              placeholder="Table #" 
              value={g.tableNumber} 
              onChange={(e) => handleChange(i, 'tableNumber', e.target.value)} 
            />
            <input
              className="input-field"
              style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
              type="file"
              accept="image/*"
              onChange={(e) => handleChange(i, 'photo', e.target.files[0])}
            />
            <label style={{ display: 'flex', alignItems: 'center', flex: '1 1 200px', gap: '8px', fontFamily: 'var(--font-body)', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}>
              <input
                type="checkbox"
                checked={g.eveningGuest || false}
                onChange={(e) => handleChange(i, 'eveningGuest', e.target.checked)}
              />
              Evening guest only
            </label>
            <select
              className="select-field"
              style={{ flex: '1 1 200px', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
              value={g.role || 'guest'}
              onChange={(e) => handleChange(i, 'role', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && i === guests.length - 1 && !e.shiftKey) {
                  e.preventDefault();
                  addGuest();
                }
              }}
            >
              <option value="guest">Guest</option>
              <option value="bride">Bride</option>
              <option value="groom">Groom</option>
              <option value="bestman">Best Man</option>
              <option value="groomsman">Groomsman</option>
              <option value="bridesmaid">Bridesmaid</option>
              <option value="maidofhonor">Maid of Honor</option>
              <option value="paigeboy">Paige Boy</option>
              <option value="flowergirl">Flower Girl</option>
            </select>
            <textarea
              className="input-field"
              style={{ 
                flex: '1 1 100%', 
                minHeight: '60px',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontFamily: 'var(--font-body)'
              }}
              placeholder="Comments"
              value={g.comments}
              onChange={(e) => handleChange(i, 'comments', e.target.value)}
            ></textarea>
            <button 
              type="button" 
              onClick={() => removeGuest(i)}
              className="button-primary"
              style={{
                backgroundColor: 'var(--button-danger)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
              }}
            >Remove</button>
          </div>
        ))}
        <button 
          type="button" 
          onClick={addGuest}
          className="button-secondary"
          style={{ 
            marginRight: '10px',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
          }}
        >Add Another Guest</button>
        <button 
          type="submit"
          className="button-primary"
          style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
        >Submit Group</button>
      </form>
    </div>
  );
}

export default AddGuest;